import React, { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Shield, Check, X, Trash2, RefreshCw, Bell } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AdminUsersPanel, AdminPostsPanel } from "@/components/AdminUsersPosts";
import AdminLinkManager from "@/components/AdminLinkManager";

const LAUNCH_STATES = ["PRE_LAUNCH", "LAUNCHING", "LIVE", "PAUSED"];
const CHAIN_STATES = ["COMING_SOON", "LAUNCHING", "LIVE", "PAUSED"];

function Stat({ label, value }) {
  return (
    <div className="comic-card p-5">
      <p className="font-mono text-xs uppercase text-dim">{label}</p>
      <p className="font-display font-900 text-neon text-3xl mt-1">{value ?? "—"}</p>
    </div>
  );
}

const inputCls = "w-full bg-ink border-3 border-black px-3 py-2 font-mono text-xs text-cream focus:outline-none focus:border-neon";

const fmtUSD = (n) => `$${(Number(n) || 0).toFixed(4)}`;
const fmtNum = (n) => (Number(n) || 0).toLocaleString();
const AI_RANGES = [{ k: "today", l: "Today" }, { k: "7d", l: "7 Days" }, { k: "30d", l: "30 Days" }];

function AiUsagePanel() {
  const [range, setRange] = useState("7d");
  const { data, isLoading } = useQuery({
    queryKey: ["ai-usage", range],
    queryFn: async () => (await api.get(`/admin/ai-usage/summary?range=${range}`)).data,
  });
  const o = data?.overview;
  const th = "text-left font-heading font-bold uppercase text-[10px] text-dim px-3 py-2";
  const td = "font-mono text-xs text-cream px-3 py-2";
  return (
    <div className="space-y-5" data-testid="ai-usage-panel">
      <div className="flex items-center gap-2">
        {AI_RANGES.map((r) => (
          <button key={r.k} onClick={() => setRange(r.k)} data-testid={`ai-range-${r.k}`}
            className={`border-3 px-3 py-1.5 font-heading font-bold uppercase text-[10px] ${range === r.k ? "border-neon bg-neon text-black" : "border-black bg-ink2 text-cream/70 hover:border-neon"}`}>
            {r.l}
          </button>
        ))}
      </div>

      {data?.audit && (
        <div className="comic-card p-4 border-l-4 !border-l-neon" data-testid="ai-audit">
          <p className="font-display font-900 uppercase text-neon text-sm">Baseline Audit</p>
          <p className="font-mono text-[11px] text-cream/90 mt-1">{data.audit.summary}</p>
          <div className="grid sm:grid-cols-2 gap-2 mt-3">
            {[["Surprise Me", data.audit.surprise_me], ["Captions", data.audit.captions], ["Object Storage", data.audit.object_storage], ["AI call sites", String(data.audit.ai_call_sites)]].map(([k, v]) => (
              <div key={k} className="border-3 border-black bg-ink2 p-2">
                <span className="font-heading font-bold uppercase text-[9px] text-neon">{k}</span>
                <p className="font-mono text-[10px] text-dim mt-0.5">{v}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="AI Requests" value={fmtNum(o?.requests)} />
        <Stat label="Total Tokens" value={fmtNum(o?.total_tokens)} />
        <Stat label="Est. Cost" value={o ? fmtUSD(o.est_cost) : "—"} />
        <Stat label="Cost / Request" value={o ? fmtUSD(o.cost_per_request) : "—"} />
        <Stat label="Input Tokens" value={fmtNum(o?.input_tokens)} />
        <Stat label="Output Tokens" value={fmtNum(o?.output_tokens)} />
        <Stat label="Active Users" value={fmtNum(o?.active_users)} />
        <Stat label="Cost / User" value={o ? fmtUSD(o.cost_per_active_user) : "—"} />
      </div>

      <div className="comic-card p-0 overflow-hidden">
        <p className="font-display font-900 uppercase text-neon text-sm px-4 pt-4">By Feature</p>
        <div className="overflow-x-auto mt-2">
          <table className="w-full">
            <thead><tr className="border-b border-charcoal">
              <th className={th}>Feature</th><th className={th}>Reqs</th><th className={th}>In</th><th className={th}>Out</th><th className={th}>Total</th><th className={th}>Est. Cost</th><th className={th}>Cost/Req</th><th className={th}>Flag</th>
            </tr></thead>
            <tbody data-testid="ai-by-feature">
              {(data?.by_feature || []).length === 0 && <tr><td className={td + " text-dim"} colSpan={8}>No AI usage recorded in this range.</td></tr>}
              {(data?.by_feature || []).map((f) => (
                <tr key={f.name} className="border-b border-charcoal/50">
                  <td className={td}>{f.name}</td><td className={td}>{fmtNum(f.requests)}</td><td className={td}>{fmtNum(f.input_tokens)}</td><td className={td}>{fmtNum(f.output_tokens)}</td><td className={td}>{fmtNum(f.total_tokens)}</td><td className={td}>{fmtUSD(f.est_cost)}</td><td className={td}>{fmtUSD(f.cost_per_request)}</td><td className={td} title={f.flag_note}>{f.flag}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="comic-card p-0 overflow-hidden">
        <p className="font-display font-900 uppercase text-neon text-sm px-4 pt-4">By Model</p>
        <div className="overflow-x-auto mt-2">
          <table className="w-full">
            <thead><tr className="border-b border-charcoal">
              <th className={th}>Model</th><th className={th}>Reqs</th><th className={th}>Total Tokens</th><th className={th}>Est. Cost</th><th className={th}>Cost/Req</th>
            </tr></thead>
            <tbody data-testid="ai-by-model">
              {(data?.by_model || []).length === 0 && <tr><td className={td + " text-dim"} colSpan={5}>No models used yet.</td></tr>}
              {(data?.by_model || []).map((m) => (
                <tr key={m.name} className="border-b border-charcoal/50">
                  <td className={td}>{m.name}</td><td className={td}>{fmtNum(m.requests)}</td><td className={td}>{fmtNum(m.total_tokens)}</td><td className={td}>{fmtUSD(m.est_cost)}</td><td className={td}>{fmtUSD(m.cost_per_request)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="comic-card p-4">
        <p className="font-display font-900 uppercase text-neon text-sm">Optimization Opportunities</p>
        <div className="mt-2 space-y-2" data-testid="ai-opportunities">
          {(data?.opportunities || []).length === 0 ? (
            <p className="font-mono text-[11px] text-dim">None flagged — nothing is over-spending. (Surprise Me & captions are $0/local.)</p>
          ) : (data.opportunities.map((op, i) => (
            <div key={i} className="border-3 border-black bg-ink2 p-2 flex items-start gap-2">
              <span>{op.flag}</span>
              <div><span className="font-heading font-bold uppercase text-[10px] text-cream">{op.name}</span>
                <p className="font-mono text-[10px] text-dim">{op.detail}</p></div>
            </div>
          )))}
        </div>
      </div>

      {isLoading && <p className="font-mono text-xs text-dim">Loading…</p>}
    </div>
  );
}

const CREATIVE_RANGES = [{ k: "today", l: "Today" }, { k: "7d", l: "7 Days" }, { k: "30d", l: "30 Days" }, { k: "all", l: "All Time" }];

function CreativeAnalyticsPanel() {
  const [range, setRange] = useState("7d");
  const { data, isLoading } = useQuery({
    queryKey: ["creative-analytics", range],
    queryFn: async () => (await api.get(`/admin/analytics/creative?range=${range}`)).data,
  });
  const styleRows = Object.entries(data?.remixByStyle || {}).sort((a, b) => b[1] - a[1]);
  const th = "text-left font-heading font-bold uppercase text-[10px] text-dim px-3 py-2";
  const td = "font-mono text-xs text-cream px-3 py-2";
  return (
    <div className="space-y-5" data-testid="creative-analytics-panel">
      <div>
        <p className="font-display font-900 uppercase text-neon text-sm">Creative Feature Analytics</p>
        <p className="font-mono text-[11px] text-dim mt-1">No-PII usage of the Meme Studio & ROBbed Remix. 100% local, $0 AI cost.</p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {CREATIVE_RANGES.map((r) => (
          <button key={r.k} onClick={() => setRange(r.k)} data-testid={`creative-range-${r.k}`}
            className={`border-3 px-3 py-1.5 font-heading font-bold uppercase text-[10px] ${range === r.k ? "border-neon bg-neon text-black" : "border-black bg-ink2 text-cream/70 hover:border-neon"}`}>
            {r.l}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="creative-stats">
        <Stat label="Memes Created" value={fmtNum(data?.memesCreated)} />
        <Stat label="Remixes Generated" value={fmtNum(data?.remixesGenerated)} />
        <Stat label="Downloads" value={fmtNum(data?.downloads)} />
        <Stat label="X Shares" value={fmtNum(data?.xShares)} />
        <Stat label="Wall Posts" value={fmtNum(data?.wallPosts)} />
        <Stat label="Battle Entries" value={fmtNum(data?.battleEntries)} />
        <Stat label="Studio Opened" value={fmtNum(data?.memeGeneratorOpened)} />
        <Stat label="Remix Opened" value={fmtNum(data?.remixOpened)} />
      </div>

      <div className="comic-card p-0 overflow-hidden">
        <p className="font-display font-900 uppercase text-neon text-sm px-4 pt-4">Remix Style Breakdown</p>
        <div className="overflow-x-auto mt-2">
          <table className="w-full">
            <thead><tr className="border-b border-charcoal">
              <th className={th}>Style</th><th className={th}>Times Selected</th>
            </tr></thead>
            <tbody data-testid="creative-remix-styles">
              {styleRows.length === 0 && <tr><td className={td + " text-dim"} colSpan={2}>No remix styles selected in this range.</td></tr>}
              {styleRows.map(([name, count]) => (
                <tr key={name} className="border-b border-charcoal/50">
                  <td className={td}>{name}</td><td className={td}>{fmtNum(count)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {isLoading && <p className="font-mono text-xs text-dim">Loading…</p>}
    </div>
  );
}

function MomentAdminPanel() {
  const { data } = useQuery({
    queryKey: ["admin-moment"],
    queryFn: async () => (await api.get("/admin/meme-of-moment")).data,
    refetchInterval: 30000,
  });
  const rows = [
    ["Gender", data?.gender], ["Style", data?.style], ["Pose", data?.poseLabel || data?.poseId],
    ["Sticker", data?.stickerType], ["Background", data?.bgId], ["Template", data?.templateId],
    ["Tone", data?.tone], ["Caption", (data?.caption || []).join(" / ")],
    ["Generated", data?.generatedAt], ["Expires", data?.expiresAt],
  ];
  return (
    <div className="space-y-5" data-testid="moment-admin-panel">
      <p className="font-display font-900 uppercase text-neon text-sm">Current Meme Of The Moment</p>
      <div className="grid lg:grid-cols-3 gap-4">
        <Stat label="Views" value={(data?.views ?? 0).toLocaleString()} />
        <Stat label="Shares" value={(data?.shares ?? 0).toLocaleString()} />
        <Stat label="Make Your Own" value={(data?.makeYourOwn ?? 0).toLocaleString()} />
      </div>
      <div className="comic-card p-0 overflow-hidden">
        <table className="w-full">
          <tbody data-testid="moment-attrs">
            {rows.map(([k, v]) => (
              <tr key={k} className="border-b border-charcoal/50">
                <td className="font-heading font-bold uppercase text-[10px] text-dim px-3 py-2 w-40">{k}</td>
                <td className="font-mono text-xs text-cream px-3 py-2 break-all">{v || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="font-mono text-[10px] text-dim">Regenerates hourly from local assets. AI token cost: $0.</p>
    </div>
  );
}

export default function Admin() {
  const qc = useQueryClient();
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const { data: overview } = useQuery({ queryKey: ["admin-overview"], queryFn: async () => (await api.get("/admin/overview")).data });
  const { data: modStories = [] } = useQuery({
    queryKey: ["admin-stories"],
    queryFn: async () => (await api.get("/admin/stories")).data,
    refetchOnWindowFocus: true, refetchOnMount: "always", staleTime: 0,
  });
  const { data: modCount } = useQuery({
    queryKey: ["admin-mod-count"],
    queryFn: async () => (await api.get("/admin/moderation/pending-count")).data,
    refetchInterval: 20000, refetchOnWindowFocus: true,
  });
  const [tab, setTab] = useState("overview");
  const pending = modCount?.pending ?? 0;
  const { data: logs = [] } = useQuery({ queryKey: ["audit-logs"], queryFn: async () => (await api.get("/admin/audit-logs")).data });
  const { data: battles = [] } = useQuery({ queryKey: ["admin-battles"], queryFn: async () => (await api.get("/battles")).data });
  const [newBattle, setNewBattle] = useState({ title: "", prompt: "", durationDays: 7 });

  const invalidateBattles = () => { qc.invalidateQueries({ queryKey: ["admin-battles"] }); qc.invalidateQueries({ queryKey: ["battles"] }); };
  const createBattle = useMutation({
    mutationFn: () => api.post("/admin/battles", { ...newBattle, durationDays: Number(newBattle.durationDays) || null, status: "active" }),
    onSuccess: () => { toast.success("Battle created"); setNewBattle({ title: "", prompt: "", durationDays: 7 }); invalidateBattles(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const battleStatus = useMutation({
    mutationFn: ({ id, status }) => api.patch(`/admin/battles/${id}`, { status }),
    onSuccess: () => { toast.success("Battle updated"); invalidateBattles(); },
  });
  const delBattle = useMutation({
    mutationFn: (id) => api.delete(`/admin/battles/${id}`),
    onSuccess: () => { toast.success("Battle deleted"); invalidateBattles(); },
  });
  const recount = useMutation({
    mutationFn: (id) => api.post(`/admin/battles/${id}/recount`),
    onSuccess: () => { toast.success("Recounted from verified votes"); invalidateBattles(); },
  });

  const { data: repCfg } = useQuery({ queryKey: ["admin-community"], queryFn: async () => (await api.get("/admin/community/config")).data });
  const [cfg, setCfg] = useState(null);
  useEffect(() => { if (repCfg) setCfg(JSON.parse(JSON.stringify(repCfg))); }, [repCfg]);
  const saveCfg = useMutation({
    mutationFn: (payload) => api.put("/admin/community/config", payload),
    onSuccess: () => {
      toast.success("Community config saved. Live now.");
      qc.invalidateQueries({ queryKey: ["admin-community"] });
      qc.invalidateQueries({ queryKey: ["community-config"] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  // --- Mascot Studio (finished-character PNGs) ---
  const { data: mascatalog } = useQuery({ queryKey: ["admin-mascot"], queryFn: async () => (await api.get("/admin/mascot/catalog")).data });
  const invalidateMascot = () => { qc.invalidateQueries({ queryKey: ["admin-mascot"] }); qc.invalidateQueries({ queryKey: ["mascot-catalog"] }); };
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const imgUrl = (src) => (!src ? "" : src.startsWith("http") ? src : `${BACKEND}${src}`);
  const discardUpload = (src) => { if (src && src.includes("/api/files/")) api.post("/admin/mascot/upload/discard", { src }).catch(() => {}); };
  const togglePose = useMutation({ mutationFn: ({ id, ...p }) => api.put(`/admin/mascot/poses/${id}`, p), onSuccess: invalidateMascot, onError: (e) => toast.error(formatApiError(e.response?.data?.detail)) });

  // Default character per pose + gender (stored as the pose base)
  const [baseForm, setBaseForm] = useState({ poseId: "", gender: "female", src: "" });
  const [baseUploading, setBaseUploading] = useState(false);
  const baseInputRef = useRef(null);
  const doBaseUpload = async (fileList) => {
    const f = fileList?.[0];
    if (!f) return;
    if (f.type !== "image/png") { toast.error("Please upload a transparent PNG."); return; }
    const prev = baseForm.src;
    setBaseUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f); fd.append("requireAlpha", "true");
      const { data } = await api.post("/admin/mascot/upload", fd);
      if (prev && prev !== data.src) discardUpload(prev);
      setBaseForm((p) => ({ ...p, src: data.src }));
      toast.success(`Uploaded ${data.width}×${data.height}px · transparent ✓. Stored as-is.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBaseUploading(false); if (baseInputRef.current) baseInputRef.current.value = ""; }
  };
  const saveBase = useMutation({
    mutationFn: ({ poseId, gender, src }) => api.post(`/admin/mascot/poses/${poseId}/base`, { gender, src }),
    onSuccess: () => { toast.success("Default character set. Live in the generator now."); setBaseForm((p) => ({ ...p, src: "" })); invalidateMascot(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const delBase = useMutation({
    mutationFn: ({ poseId, gender }) => api.delete(`/admin/mascot/poses/${poseId}/base/${gender}`),
    onSuccess: () => { toast.success("Default character removed"); invalidateMascot(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  // Additional finished character variants (mascot_characters)
  const blankChar = { poseId: "", gender: "female", name: "", src: "", isDefault: false, order: 0 };
  const [charForm, setCharForm] = useState(blankChar);
  const [charUploading, setCharUploading] = useState(false);
  const charInputRef = useRef(null);
  const doCharUpload = async (fileList) => {
    const f = fileList?.[0];
    if (!f) return;
    if (f.type !== "image/png") { toast.error("Please upload a transparent PNG."); return; }
    const prev = charForm.src;
    setCharUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f); fd.append("requireAlpha", "true");
      const { data } = await api.post("/admin/mascot/upload", fd);
      if (prev && prev !== data.src) discardUpload(prev);
      setCharForm((p) => ({ ...p, src: data.src }));
      toast.success(`Uploaded ${data.width}×${data.height}px · transparent ✓. Stored as-is.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setCharUploading(false); if (charInputRef.current) charInputRef.current.value = ""; }
  };
  const saveChar = useMutation({
    mutationFn: (payload) => api.post("/admin/mascot/characters", payload),
    onSuccess: () => { toast.success("Finished character added. Live in the generator now."); setCharForm(blankChar); invalidateMascot(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const updateChar = useMutation({ mutationFn: ({ id, ...patch }) => api.put(`/admin/mascot/characters/${id}`, patch), onSuccess: invalidateMascot, onError: (e) => toast.error(formatApiError(e.response?.data?.detail)) });
  const delChar = useMutation({ mutationFn: (id) => api.delete(`/admin/mascot/characters/${id}`), onSuccess: () => { toast.success("Character removed"); invalidateMascot(); }, onError: (e) => toast.error(formatApiError(e.response?.data?.detail)) });

  // --- Short Hair collection review ---
  const REVIEW_ORDER = ["pointing", "shrug", "chart", "moneybag", "crate", "jump", "stare", "throne", "crying"];
  const [lightbox, setLightbox] = useState(null); // {src, label}
  const [replacingId, setReplacingId] = useState(null);
  const doReplaceUpload = async (charId, fileList) => {
    const f = fileList?.[0];
    if (!f) return;
    if (f.type !== "image/png") { toast.error("Replacement must be a transparent PNG."); return; }
    setReplacingId(charId);
    try {
      const fd = new FormData(); fd.append("file", f); fd.append("requireAlpha", "true");
      const { data } = await api.post("/admin/mascot/upload", fd);
      await api.put(`/admin/mascot/characters/${charId}`, { src: data.src });
      toast.success(`Replaced (${data.width}×${data.height}px). Rendered as-is.`);
      invalidateMascot();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setReplacingId(null); }
  };

  const [launchState, setLaunchState] = useState("PRE_LAUNCH");
  const [announcement, setAnnouncement] = useState("");
  const [announcementActive, setAnnouncementActive] = useState(true);
  const [xUrl, setXUrl] = useState("");
  const [chains, setChains] = useState([]);

  useEffect(() => {
    if (settings) {
      setLaunchState(settings.launchState);
      setAnnouncement(settings.announcement || "");
      setAnnouncementActive(!!settings.announcementActive);
      setXUrl(settings.xUrl || "");
      setChains(JSON.parse(JSON.stringify(settings.chains || [])));
    }
  }, [settings]);

  const save = useMutation({
    mutationFn: (payload) => api.put("/settings", payload),
    onSuccess: () => {
      toast.success("Saved. Changes are live.");
      qc.invalidateQueries({ queryKey: ["settings"] });
      qc.invalidateQueries({ queryKey: ["admin-overview"] });
      qc.invalidateQueries({ queryKey: ["audit-logs"] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const moderate = useMutation({
    mutationFn: ({ id, status }) => api.patch(`/admin/stories/${id}`, { status }),
    onSuccess: () => { toast.success("Updated"); qc.invalidateQueries({ queryKey: ["admin-stories"] }); qc.invalidateQueries({ queryKey: ["stories"] }); qc.invalidateQueries({ queryKey: ["admin-mod-count"] }); },
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/admin/stories/${id}`),
    onSuccess: () => { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["admin-stories"] }); qc.invalidateQueries({ queryKey: ["stories"] }); qc.invalidateQueries({ queryKey: ["admin-mod-count"] }); },
  });
  // Bulk moderation
  const [sel, setSel] = useState(() => new Set());
  const toggleSel = (id) => setSel((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const clearSel = () => setSel(new Set());
  const bulk = useMutation({
    mutationFn: ({ ids, action }) => api.post("/admin/stories/bulk", { ids, action }),
    onSuccess: (res) => {
      const { action, processed } = res.data;
      toast.success(`${action === "delete" ? "Removed" : action === "approve" ? "Approved" : "Rejected"} ${processed} post${processed === 1 ? "" : "s"}`);
      clearSel();
      qc.invalidateQueries({ queryKey: ["admin-stories"] });
      qc.invalidateQueries({ queryKey: ["stories"] });
      qc.invalidateQueries({ queryKey: ["admin-mod-count"] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const updateChain = (idx, key, val) => {
    const next = [...chains];
    next[idx] = { ...next[idx], [key]: val === "" ? null : val };
    setChains(next);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Shield className="w-8 h-8 text-neon" />
          <h1 className="headline text-cream text-3xl sm:text-4xl">ADMIN CONTROL</h1>
        </div>
        <button
          onClick={() => setTab("moderation")}
          data-testid="admin-mod-badge"
          className={`relative inline-flex items-center gap-2 border-3 border-black px-4 py-2 font-heading font-bold uppercase text-xs transition-colors ${pending > 0 ? "bg-gold text-black animate-pulse" : "bg-ink2 text-dim hover:text-cream"}`}>
          <Bell className="w-4 h-4" />
          Moderation
          {pending > 0 && (
            <span data-testid="admin-mod-badge-count"
              className="min-w-[22px] h-[22px] px-1.5 grid place-items-center bg-danger text-white border-2 border-black rounded-full font-display font-900 text-[11px] leading-none">
              {pending}
            </span>
          )}
        </button>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-8">
        <TabsList className="bg-ink2 border-3 border-black flex-wrap h-auto">
          {["overview", "users", "posts", "links", "launch", "chains", "moderation", "battles", "community", "mascot", "moment", "creative", "ai-usage", "audit"].map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`tab-${t}`}
              className="font-heading font-bold uppercase text-xs data-[state=active]:bg-neon data-[state=active]:text-black text-cream/70">
              {t === "moderation" && pending > 0 ? `${t} (${pending})` : t}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat label="Launch State" value={overview?.launchState?.replace("_", "-")} />
            <Stat label="Total Users" value={overview?.totalUsers} />
            <Stat label="Approved Stories" value={overview?.approvedStories} />
            <Stat label="Pending Stories" value={overview?.pendingStories} />
          </div>
        </TabsContent>

        <TabsContent value="users" className="mt-6" data-testid="tab-content-users">
          <AdminUsersPanel />
        </TabsContent>

        <TabsContent value="posts" className="mt-6" data-testid="tab-content-posts">
          <AdminPostsPanel />
        </TabsContent>

        <TabsContent value="links" className="mt-6" data-testid="tab-content-links">
          <AdminLinkManager />
        </TabsContent>

        <TabsContent value="launch" className="mt-6">
          <div className="comic-card p-6 space-y-5 max-w-xl">
            <div>
              <label className="font-display font-900 uppercase text-xs text-neon">Project Launch State</label>
              <Select value={launchState} onValueChange={setLaunchState}>
                <SelectTrigger className="mt-2 bg-ink border-3 border-black text-cream font-mono text-sm" data-testid="admin-launch-state"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-ink2 border-3 border-black text-cream">
                  {LAUNCH_STATES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", "-")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="font-display font-900 uppercase text-xs text-neon">Announcement Banner</label>
              <textarea rows={2} value={announcement} onChange={(e) => setAnnouncement(e.target.value)} className={`${inputCls} mt-2 resize-none`} data-testid="admin-announcement" />
            </div>
            <label className="flex items-center gap-3 font-mono text-sm text-cream">
              <input type="checkbox" checked={announcementActive} onChange={(e) => setAnnouncementActive(e.target.checked)} className="w-5 h-5 accent-neon" data-testid="admin-announcement-active" />
              Show announcement banner
            </label>
            <div>
              <label className="font-display font-900 uppercase text-xs text-neon">Official X URL</label>
              <input value={xUrl} onChange={(e) => setXUrl(e.target.value)} className={`${inputCls} mt-2`} data-testid="admin-xurl" />
            </div>
            <button onClick={() => save.mutate({ launchState, announcement, announcementActive, xUrl })}
              className="btn-neon" data-testid="admin-save-launch">Save Launch Config</button>
          </div>
        </TabsContent>

        <TabsContent value="chains" className="mt-6">
          <div className="grid lg:grid-cols-2 gap-6">
            {chains.map((c, idx) => (
              <div key={c.chainName} className="comic-card p-6 space-y-3" data-testid={`admin-chain-${idx}`}>
                <h3 className="font-display font-900 text-neon text-lg uppercase">{c.chainName}</h3>
                <div>
                  <label className="font-mono text-[10px] uppercase text-dim">Status</label>
                  <Select value={c.status} onValueChange={(v) => updateChain(idx, "status", v)}>
                    <SelectTrigger className="mt-1 bg-ink border-3 border-black text-cream font-mono text-xs" data-testid={`admin-chain-status-${idx}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-ink2 border-3 border-black text-cream">
                      {CHAIN_STATES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {[
                  [c.chainType === "solana" ? "mintAddress" : "tokenAddress", c.chainType === "solana" ? "Mint Address" : "Contract Address"],
                  ...(c.chainType === "evm" ? [["chainId", "EVM Chain ID"], ["rpcUrl", "RPC URL"], ["nativeCurrencySymbol", "Native Currency Symbol"]] : []),
                  ["explorerUrl", "Explorer URL"],
                  ["dexUrl", "DEX URL"],
                  ["poolAddress", "Pool Address"],
                  ["liquidityAddress", "Liquidity Address"],
                  ["treasuryAddress", "Treasury Wallet"],
                  ["rewardsAddress", "Rewards Wallet"],
                  ["teamAddress", "Team Wallet"],
                ].map(([key, label]) => (
                  <div key={key}>
                    <label className="font-mono text-[10px] uppercase text-dim">{label}</label>
                    <input value={c[key] || ""} onChange={(e) => updateChain(idx, key, e.target.value)} className={`${inputCls} mt-1`} placeholder="—" />
                  </div>
                ))}
                <label className="flex items-center gap-2 font-mono text-xs text-cream pt-1">
                  <input type="checkbox" checked={!!c.officialContractVerified} onChange={(e) => updateChain(idx, "officialContractVerified", e.target.checked)} className="w-4 h-4 accent-neon" />
                  Officially verified
                </label>
              </div>
            ))}
          </div>
          <button onClick={() => save.mutate({ chains })} className="btn-neon mt-6" data-testid="admin-save-chains">Save Chain Config</button>
        </TabsContent>

        <TabsContent value="moderation" className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <p className="font-mono text-sm text-dim">{modStories.length} submissions</p>
            <div className="flex items-center gap-2">
              {modStories.some((s) => s.status === "pending") && (
                <button
                  onClick={() => setSel(new Set(modStories.filter((s) => s.status === "pending").map((s) => s.id)))}
                  className="btn-ghost !py-1.5 !px-3 !text-xs" data-testid="bulk-select-pending">Select Pending</button>
              )}
              <button onClick={() => qc.invalidateQueries({ queryKey: ["admin-stories"] })} className="btn-ghost !py-1.5 !px-3 !text-xs"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
            </div>
          </div>

          {sel.size > 0 && (
            <div className="comic-card p-3 mb-4 flex flex-wrap items-center gap-2 bg-ink3 sticky top-2 z-20" data-testid="bulk-action-bar">
              <span className="font-display font-900 text-neon text-sm mr-1" data-testid="bulk-selected-count">{sel.size} selected</span>
              <button disabled={bulk.isPending} onClick={() => bulk.mutate({ ids: [...sel], action: "approve" })} className="border-3 border-black bg-neon text-black px-3 py-1.5 font-heading font-bold uppercase text-[11px] disabled:opacity-40" data-testid="bulk-approve"><Check className="w-3.5 h-3.5 inline" /> Approve</button>
              <button disabled={bulk.isPending} onClick={() => bulk.mutate({ ids: [...sel], action: "reject" })} className="border-3 border-black bg-ink2 text-danger px-3 py-1.5 font-heading font-bold uppercase text-[11px] disabled:opacity-40" data-testid="bulk-reject"><X className="w-3.5 h-3.5 inline" /> Reject</button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button disabled={bulk.isPending} className="border-3 border-black bg-danger text-white px-3 py-1.5 font-heading font-bold uppercase text-[11px] disabled:opacity-40" data-testid="bulk-delete"><Trash2 className="w-3.5 h-3.5 inline" /> Delete</button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-ink2 border-3 border-black text-cream">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="font-display font-900 uppercase text-danger">Remove {sel.size} post{sel.size === 1 ? "" : "s"}?</AlertDialogTitle>
                    <AlertDialogDescription className="font-mono text-xs text-dim">This permanently deletes all selected submissions and can't be undone.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="border-3 border-black bg-ink3 text-cream font-heading uppercase text-xs" data-testid="bulk-delete-cancel">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => bulk.mutate({ ids: [...sel], action: "delete" })} className="border-3 border-black bg-danger text-white font-heading uppercase text-xs" data-testid="bulk-delete-confirm">Delete {sel.size}</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <button onClick={clearSel} className="btn-ghost !py-1.5 !px-3 !text-[11px]" data-testid="bulk-clear">Clear</button>
            </div>
          )}

          <div className="space-y-3">
            {modStories.map((s) => (
              <div key={s.id} className={`comic-card p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${sel.has(s.id) ? "!border-neon" : ""}`} data-testid={`mod-story-${s.id}`}>
                <input type="checkbox" checked={sel.has(s.id)} onChange={() => toggleSel(s.id)} className="w-5 h-5 accent-neon shrink-0 self-start sm:self-center cursor-pointer" data-testid={`mod-select-${s.id}`} />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`tag !text-[9px] !px-2 !py-0.5 ${s.status === "approved" ? "bg-neon" : s.status === "rejected" ? "bg-danger text-white" : "bg-gold"}`}>{s.status}</span>
                    <span className="font-mono text-[10px] text-dim">@{s.username}</span>
                  </div>
                  {s.image_url && (
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${s.image_url}`} alt="meme"
                      className="mt-2 max-h-40 border-3 border-black bg-white" data-testid={`mod-image-${s.id}`} />
                  )}
                  {s.story && <p className="font-mono text-sm text-cream/90 mt-2">"{s.story}"</p>}
                </div>
                <div className="flex gap-2">
                  <button disabled={s.status === "approved"} onClick={() => moderate.mutate({ id: s.id, status: "approved" })} className="border-3 border-black bg-neon text-black p-2 disabled:opacity-40" data-testid={`approve-${s.id}`}><Check className="w-4 h-4" /></button>
                  <button disabled={s.status === "rejected"} onClick={() => moderate.mutate({ id: s.id, status: "rejected" })} className="border-3 border-black bg-ink3 text-danger p-2 disabled:opacity-40" data-testid={`reject-${s.id}`}><X className="w-4 h-4" /></button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button className="border-3 border-black bg-ink3 text-cream p-2" data-testid={`del-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-ink2 border-3 border-black text-cream">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="font-display font-900 uppercase text-danger">Remove this post?</AlertDialogTitle>
                        <AlertDialogDescription className="font-mono text-xs text-dim">This permanently deletes the submission and can't be undone.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="border-3 border-black bg-ink3 text-cream font-heading uppercase text-xs" data-testid={`del-cancel-${s.id}`}>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => del.mutate(s.id)} className="border-3 border-black bg-danger text-white font-heading uppercase text-xs" data-testid={`del-confirm-${s.id}`}>Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="battles" className="mt-6">
          <div className="comic-card p-6 max-w-xl mb-6">
            <h3 className="font-display font-900 text-neon uppercase text-sm">Create Battle</h3>
            <input className={`${inputCls} mt-3`} placeholder="Title (e.g. WHO GOT ROBBED WORSE?)" value={newBattle.title}
              onChange={(e) => setNewBattle({ ...newBattle, title: e.target.value })} data-testid="battle-title" />
            <input className={`${inputCls} mt-3`} placeholder="Prompt (optional)" value={newBattle.prompt}
              onChange={(e) => setNewBattle({ ...newBattle, prompt: e.target.value })} data-testid="battle-prompt" />
            <div className="mt-3">
              <label className="font-mono text-[10px] uppercase text-dim">Runs for (days)</label>
              <input type="number" min="1" max="60" className={`${inputCls} mt-1`} value={newBattle.durationDays}
                onChange={(e) => setNewBattle({ ...newBattle, durationDays: e.target.value })} data-testid="battle-duration" />
            </div>
            <button onClick={() => createBattle.mutate()} disabled={newBattle.title.length < 2} className="btn-neon mt-4 disabled:opacity-50" data-testid="battle-create">Create (Active)</button>
          </div>
          <div className="space-y-3">
            {battles.map((b) => (
              <div key={b.id} className="comic-card p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`admin-battle-${b.id}`}>
                <div className="flex-1">
                  <p className="font-display font-900 text-cream uppercase text-sm">#{String(b.number || 1).padStart(3, "0")} · {b.title}</p>
                  <p className="font-mono text-[10px] text-dim mt-1">{b.submissionCount || 0} entries · {b.status}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={b.status} onValueChange={(v) => battleStatus.mutate({ id: b.id, status: v })}>
                    <SelectTrigger className="w-32 bg-ink border-3 border-black text-cream font-mono text-xs" data-testid={`battle-status-${b.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-ink2 border-3 border-black text-cream">
                      {["upcoming", "active", "voting", "ended"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <button onClick={() => recount.mutate(b.id)} className="border-3 border-black bg-ink3 text-neon p-2" title="Recount from verified votes" data-testid={`battle-recount-${b.id}`}><RefreshCw className="w-4 h-4" /></button>
                  <button onClick={() => delBattle.mutate(b.id)} className="border-3 border-black bg-ink3 text-danger p-2" data-testid={`battle-del-${b.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
            {battles.length === 0 && <p className="font-mono text-sm text-dim">No battles yet. Create one above.</p>}
          </div>
          <p className="font-mono text-[11px] text-dim mt-4">Battles auto-close and auto-crown the top-voted entry at their deadline. Set status to "ended" to crown early. Recount pulls authoritative counts from verified votes.</p>
        </TabsContent>

        <TabsContent value="community" className="mt-6">
          {!cfg ? <p className="font-mono text-dim">Loading config...</p> : (
            <div className="space-y-6 max-w-3xl">
              <p className="font-mono text-[11px] text-dim">ROBBED REP is off-chain community reputation only — never a token balance or monetary reward. All values below are live and server-enforced.</p>

              <div className="comic-card p-5">
                <h3 className="font-display font-900 text-neon uppercase text-sm">REP Values</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
                  {Object.keys(cfg.values || {}).map((k) => (
                    <div key={k}>
                      <label className="font-mono text-[10px] uppercase text-dim">{k.replace(/_/g, " ")}</label>
                      <input type="number" className={`${inputCls} mt-1`} value={cfg.values[k]}
                        onChange={(e) => setCfg({ ...cfg, values: { ...cfg.values, [k]: Number(e.target.value) } })}
                        data-testid={`rep-val-${k}`} />
                    </div>
                  ))}
                </div>
              </div>

              <div className="comic-card p-5 grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="font-mono text-[10px] uppercase text-dim">Finalists per battle</label>
                  <input type="number" min="1" max="20" className={`${inputCls} mt-1`} value={cfg.finalistCount}
                    onChange={(e) => setCfg({ ...cfg, finalistCount: Number(e.target.value) })} data-testid="cfg-finalist-count" />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase text-dim">Community Favorite vote threshold</label>
                  <input type="number" min="1" className={`${inputCls} mt-1`} value={cfg.favoriteThreshold}
                    onChange={(e) => setCfg({ ...cfg, favoriteThreshold: Number(e.target.value) })} data-testid="cfg-fav-threshold" />
                </div>
              </div>

              <div className="comic-card p-5">
                <h3 className="font-display font-900 text-neon uppercase text-sm">ROB Levels (name · min REP)</h3>
                <div className="space-y-2 mt-3">
                  {(cfg.levels || []).map((lv, idx) => (
                    <div key={lv.level} className="flex items-center gap-2">
                      <span className="font-display font-900 text-neon w-10 shrink-0">L{lv.level}</span>
                      <input className={`${inputCls} !w-auto flex-1 min-w-0`} value={lv.name}
                        onChange={(e) => { const n = [...cfg.levels]; n[idx] = { ...lv, name: e.target.value }; setCfg({ ...cfg, levels: n }); }}
                        data-testid={`cfg-level-name-${lv.level}`} />
                      <input type="number" className={`${inputCls} !w-24`} value={lv.min}
                        onChange={(e) => { const n = [...cfg.levels]; n[idx] = { ...lv, min: Number(e.target.value) }; setCfg({ ...cfg, levels: n }); }}
                        data-testid={`cfg-level-min-${lv.level}`} />
                    </div>
                  ))}
                </div>
              </div>

              <div className="comic-card p-5">
                <h3 className="font-display font-900 text-neon uppercase text-sm">Badges (enable · threshold)</h3>
                <div className="space-y-2 mt-3">
                  {(cfg.badges || []).map((b, idx) => (
                    <div key={b.id} className="flex items-center gap-3">
                      <input type="checkbox" checked={b.enabled !== false} className="w-5 h-5 accent-neon"
                        onChange={(e) => { const n = [...cfg.badges]; n[idx] = { ...b, enabled: e.target.checked }; setCfg({ ...cfg, badges: n }); }}
                        data-testid={`cfg-badge-enabled-${b.id}`} />
                      <span className="font-heading font-bold uppercase text-xs text-cream flex-1">{b.icon} {b.name}</span>
                      <input type="number" min="1" className={`${inputCls} !w-24`} value={b.threshold}
                        onChange={(e) => { const n = [...cfg.badges]; n[idx] = { ...b, threshold: Number(e.target.value) }; setCfg({ ...cfg, badges: n }); }}
                        data-testid={`cfg-badge-threshold-${b.id}`} />
                    </div>
                  ))}
                </div>
              </div>

              <button onClick={() => saveCfg.mutate(cfg)} className="btn-neon" data-testid="save-community-config">Save Community Config</button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="mascot" className="mt-6">
          <p className="font-mono text-[11px] text-dim">Mascot Studio — manage complete, finished mascot PNGs. Each pose renders ONE ready-made character (default per gender, plus optional finished variants). Nothing is cropped, stretched, repositioned, recolored, or layered.</p>

          {(mascatalog?.styleRefs || []).length > 0 && (
            <div className="comic-card p-4 mt-3" data-testid="style-refs">
              <h3 className="font-display font-900 text-gold uppercase text-sm">Character Style Master References</h3>
              <p className="font-mono text-[10px] text-dim mt-1">Approved style DNA references (guidance only — NOT generator variants). Use these to keep a style consistent across all 9 poses.</p>
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                {mascatalog.styleRefs.map((r) => (
                  <div key={r.id} className="border-3 border-gold bg-ink2 p-2 flex gap-3" data-testid={`style-ref-${r.styleNumber}`}>
                    <button onClick={() => setLightbox({ src: r.src, label: `Style #${r.styleNumber} — ${r.styleName}` })}
                      className="w-24 shrink-0 aspect-[2/3] border-2 border-black bg-ink3 overflow-hidden" data-testid={`style-ref-preview-${r.styleNumber}`}>
                      <img src={imgUrl(r.src)} alt={r.styleName} className="w-full h-full object-contain" />
                    </button>
                    <div className="min-w-0">
                      <p className="font-heading font-bold uppercase text-cream text-xs">Style #{r.styleNumber} · {r.styleName}</p>
                      <p className="font-mono text-[9px] text-dim mt-1 line-clamp-4">{r.dna}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Short Hair collection review — Original vs Short Hair, per pose (Female) */}
          {(() => {
            const poseById = Object.fromEntries((mascatalog?.poses || []).map((p) => [p.id, p]));
            const defSrc = (pid) => {
              const ls = (poseById[pid]?.baseVariants?.female || []);
              return (ls.find((l) => l.role === "body-base") || ls[0])?.src;
            };
            const shOf = (pid) => (mascatalog?.characters || []).find((c) => c.poseId === pid && c.gender === "female" && c.name === "Short Hair");
            const done = REVIEW_ORDER.filter((pid) => shOf(pid)).length;
            return (
              <div className="comic-card p-4 mt-3" data-testid="shorthair-review">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h3 className="font-display font-900 text-neon uppercase text-sm">Short Hair — Collection Review (Female)</h3>
                  <span className="tag !text-[9px] bg-neon text-black">{done}/9 variants</span>
                </div>
                <p className="font-mono text-[10px] text-dim mt-1">Original (current default) vs Short Hair, per pose. Review-only — defaults are unchanged. Use Replace to re-upload a variant, or Preview to view larger.</p>
                <div className="space-y-2 mt-3">
                  {REVIEW_ORDER.map((pid) => {
                    const pose = poseById[pid]; const sh = shOf(pid); const dsrc = defSrc(pid);
                    const cell = (src, label) => (
                      <div className="flex-1 min-w-0 text-center">
                        <div className="aspect-square border-3 border-black bg-ink3 flex items-center justify-center overflow-hidden"
                          style={{ backgroundImage: "linear-gradient(45deg,#2a2a2a 25%,transparent 25%,transparent 75%,#2a2a2a 75%),linear-gradient(45deg,#2a2a2a 25%,#1e1e1e 25%,#1e1e1e 75%,#2a2a2a 75%)", backgroundSize: "18px 18px", backgroundPosition: "0 0,9px 9px" }}>
                          {src ? <img src={imgUrl(src)} alt={label} className="max-w-full max-h-full object-contain" /> : <span className="font-mono text-[9px] text-danger">missing</span>}
                        </div>
                        <span className="block mt-1 font-mono text-[9px] uppercase text-dim">{label}</span>
                      </div>
                    );
                    return (
                      <div key={pid} className="border-3 border-black bg-ink2 p-2" data-testid={`review-row-${pid}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-heading font-bold uppercase text-cream text-xs">{pose?.label || pid}</span>
                          <div className="flex items-center gap-2">
                            {sh
                              ? <span className={`tag !text-[8px] ${sh.enabled !== false ? "bg-neon text-black" : "bg-ink3 text-dim"}`}>{sh.enabled !== false ? "enabled" : "disabled"}{sh.isDefault ? " · default" : ""}</span>
                              : <span className="tag !text-[8px] bg-danger text-white">no short hair</span>}
                          </div>
                        </div>
                        <div className="flex gap-3 items-start">
                          {cell(dsrc, "Original")}
                          {cell(sh?.src, "Short Hair")}
                        </div>
                        {sh && (
                          <div className="flex flex-wrap gap-2 mt-2 justify-end">
                            <span className="font-mono text-[9px] uppercase text-neon self-center" data-testid={`review-keep-${pid}`}>✓ Keep</span>
                            <button onClick={() => setLightbox({ src: sh.src, label: `${pose?.label || pid} — Short Hair` })}
                              data-testid={`review-preview-${pid}`} className="border-2 border-charcoal text-cream/80 font-mono text-[9px] uppercase px-2 py-1 hover:border-neon">Preview larger</button>
                            <label className="border-2 border-gold text-gold font-mono text-[9px] uppercase px-2 py-1 cursor-pointer hover:bg-gold hover:text-black" data-testid={`review-replace-${pid}`}>
                              {replacingId === sh.id ? "Uploading…" : "Replace"}
                              <input type="file" accept="image/png" className="hidden" onChange={(e) => doReplaceUpload(sh.id, e.target.files)} />
                            </label>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {lightbox && (
            <div className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-6" data-testid="review-lightbox" onClick={() => setLightbox(null)}>
              <div className="max-w-[90vw] max-h-[90vh] text-center" onClick={(e) => e.stopPropagation()}>
                <div className="inline-block border-3 border-neon p-2" style={{ backgroundImage: "linear-gradient(45deg,#2a2a2a 25%,transparent 25%,transparent 75%,#2a2a2a 75%),linear-gradient(45deg,#2a2a2a 25%,#1e1e1e 25%,#1e1e1e 75%,#2a2a2a 75%)", backgroundSize: "24px 24px", backgroundPosition: "0 0,12px 12px" }}>
                  <img src={imgUrl(lightbox.src)} alt={lightbox.label} className="max-w-[85vw] max-h-[78vh] object-contain" />
                </div>
                <p className="font-heading font-bold uppercase text-neon text-sm mt-3">{lightbox.label}</p>
                <button onClick={() => setLightbox(null)} className="btn-neon mt-2" data-testid="review-lightbox-close">Close</button>
              </div>
            </div>
          )}

          <h3 className="font-display font-900 text-neon uppercase text-sm mt-6">Poses ({mascatalog?.poses?.length || 0})</h3>
          <div className="grid sm:grid-cols-2 gap-2 mt-2">
            {(mascatalog?.poses || []).map((p) => (
              <div key={p.id} className="comic-card p-3 flex items-center justify-between" data-testid={`admin-pose-${p.id}`}>
                <div className="min-w-0">
                  <p className="font-heading font-bold uppercase text-cream text-xs truncate">{p.label} <span className="text-dim">· {p.character}</span></p>
                  <p className="font-mono text-[10px] text-dim truncate">{p.id} · {p.production ? "production" : "placeholder"}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {["female", "male"].map((g) => {
                      const has = Array.isArray(p.baseVariants?.[g]) && p.baseVariants[g].length > 0;
                      return (
                        <span key={g} data-testid={`admin-pose-${p.id}-base-${g}`}
                          className={`tag !text-[8px] !px-1.5 !py-0.5 ${has ? "bg-neon text-black" : "bg-ink3 text-dim"}`}>
                          {g} {has ? "✓ default" : "—"}
                          {has && (
                            <button type="button" onClick={() => delBase.mutate({ poseId: p.id, gender: g })}
                              data-testid={`admin-pose-${p.id}-base-del-${g}`}
                              className="ml-1 text-danger">✕</button>
                          )}
                        </span>
                      );
                    })}
                    {["female", "male"].map((g) => {
                      const n = (mascatalog?.characters || []).filter((c) => c.poseId === p.id && c.gender === g).length;
                      return n > 0 ? <span key={`v-${g}`} className="tag !text-[8px] !px-1.5 !py-0.5 bg-grape text-cream">{g} +{n} variant{n > 1 ? "s" : ""}</span> : null;
                    })}
                  </div>
                </div>
                <label className="flex items-center gap-1 font-mono text-[10px] text-dim shrink-0">
                  <input type="checkbox" checked={p.enabled !== false} className="w-4 h-4 accent-neon"
                    onChange={(e) => togglePose.mutate({ ...p, enabled: e.target.checked })} data-testid={`admin-pose-toggle-${p.id}`} /> on
                </label>
              </div>
            ))}
          </div>

          <h3 className="font-display font-900 text-neon uppercase text-sm mt-6">Set Default Character (per pose + gender)</h3>
          <p className="font-mono text-[11px] text-dim mt-1">Upload the finished DEFAULT character PNG for a pose + gender. Pose IDs never change — the gender toggle in the generator picks which default renders. Stored byte-for-byte (no crop/resize).</p>
          <div className="comic-card p-4 mt-2 space-y-3" data-testid="set-base-panel">
            <div>
              <label className="font-mono text-[10px] uppercase text-dim">Pose</label>
              <select value={baseForm.poseId} onChange={(e) => setBaseForm({ ...baseForm, poseId: e.target.value })}
                data-testid="base-pose-select" className="w-full bg-ink2 border-3 border-black text-cream font-mono text-xs p-2 mt-1">
                <option value="">Select a pose…</option>
                {(mascatalog?.poses || []).map((p) => <option key={p.id} value={p.id}>{`${p.label} (${p.id})`}</option>)}
              </select>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase text-dim">Character</label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {["female", "male"].map((g) => (
                  <button key={g} type="button" onClick={() => setBaseForm({ ...baseForm, gender: g })}
                    data-testid={`base-gender-${g}`}
                    className={`border-3 px-2 py-2 font-heading font-bold uppercase text-[11px] ${baseForm.gender === g ? "border-neon bg-neon text-black" : "border-black bg-ink2 text-cream/80"}`}>{g}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase text-dim">Finished character PNG (transparent)</label>
              <input ref={baseInputRef} type="file" accept="image/png" onChange={(e) => doBaseUpload(e.target.files)}
                data-testid="base-file-input" className="block w-full text-[11px] text-cream mt-1 file:mr-3 file:border-3 file:border-black file:bg-neon file:text-black file:px-3 file:py-1.5 file:font-bold file:uppercase" />
              {baseUploading && <p className="font-mono text-[10px] text-gold mt-1">Uploading…</p>}
              {baseForm.src && (
                <div className="mt-2 inline-block border-3 border-black p-1" style={{ backgroundImage: "linear-gradient(45deg,#333 25%,transparent 25%,transparent 75%,#333 75%),linear-gradient(45deg,#333 25%,#222 25%,#222 75%,#333 75%)", backgroundSize: "16px 16px", backgroundPosition: "0 0,8px 8px" }}>
                  <img src={imgUrl(baseForm.src)} alt="character preview" className="max-h-40" data-testid="base-preview" />
                </div>
              )}
            </div>
            <button onClick={() => saveBase.mutate(baseForm)} disabled={!baseForm.poseId || !baseForm.src || saveBase.isPending}
              className="btn-neon disabled:opacity-40 disabled:cursor-not-allowed" data-testid="base-save">Set {baseForm.gender} default</button>
            {(!baseForm.poseId || !baseForm.src) && (
              <p className="font-mono text-[10px] text-dim">Select a pose{!baseForm.src ? " and upload a PNG" : ""} to enable.</p>
            )}
          </div>

          <h3 className="font-display font-900 text-neon uppercase text-sm mt-6">Finished Character Variants ({mascatalog?.characters?.length || 0})</h3>
          {(mascatalog?.characters || []).length === 0
            ? <p className="font-mono text-[11px] text-dim mt-2">No extra variants yet. Each pose shows its default character; add alternate finished PNGs (different hair, skin, outfit — any complete look) below. They appear in the generator under the matching pose + gender.</p>
            : (
              <div className="grid sm:grid-cols-2 gap-2 mt-2">
                {mascatalog.characters.map((c) => {
                  const pose = (mascatalog.poses || []).find((p) => p.id === c.poseId);
                  return (
                    <div key={c.id} className="comic-card p-3 flex items-center gap-3" data-testid={`admin-char-${c.id}`}>
                      <div className="w-14 h-14 shrink-0 border-3 border-black bg-ink3 flex items-center justify-center overflow-hidden">
                        <img src={imgUrl(c.src)} alt={c.name} className="max-w-full max-h-full object-contain" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-heading font-bold uppercase text-cream text-xs truncate">{c.name} {c.isDefault && <span className="tag !text-[8px] !px-1 bg-gold text-black">DEFAULT</span>}</p>
                        <p className="font-mono text-[10px] text-dim truncate">{pose?.label || c.poseId} · {c.gender}</p>
                        <div className="flex gap-2 mt-1">
                          <button onClick={() => updateChar.mutate({ id: c.id, isDefault: true })} className="font-mono text-[9px] uppercase text-neon underline" data-testid={`admin-char-default-${c.id}`}>set default</button>
                          <button onClick={() => updateChar.mutate({ id: c.id, enabled: !(c.enabled !== false) })} className="font-mono text-[9px] uppercase text-cream/70 underline" data-testid={`admin-char-toggle-${c.id}`}>{c.enabled !== false ? "disable" : "enable"}</button>
                        </div>
                      </div>
                      <button onClick={() => delChar.mutate(c.id)} className="border-3 border-black bg-ink3 text-danger p-2 shrink-0" data-testid={`admin-char-del-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  );
                })}
              </div>
            )}

          <h3 className="font-display font-900 text-neon uppercase text-sm mt-6">Add Finished Character Variant</h3>
          <p className="font-mono text-[11px] text-dim mt-1">Upload a complete, ready-made character PNG for a specific pose + gender. Drawn exactly as-is.</p>
          <div className="comic-card p-4 mt-2 space-y-3" data-testid="add-char-panel">
            <div>
              <label className="font-mono text-[10px] uppercase text-dim">Pose</label>
              <select value={charForm.poseId} onChange={(e) => setCharForm({ ...charForm, poseId: e.target.value })}
                data-testid="char-pose-select" className="w-full bg-ink2 border-3 border-black text-cream font-mono text-xs p-2 mt-1">
                <option value="">Select a pose…</option>
                {(mascatalog?.poses || []).map((p) => <option key={p.id} value={p.id}>{`${p.label} (${p.id})`}</option>)}
              </select>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase text-dim">Character</label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {["female", "male"].map((g) => (
                  <button key={g} type="button" onClick={() => setCharForm({ ...charForm, gender: g })} data-testid={`char-gender-${g}`}
                    className={`border-3 px-2 py-2 font-heading font-bold uppercase text-[11px] ${charForm.gender === g ? "border-neon bg-neon text-black" : "border-black bg-ink2 text-cream/80"}`}>{g}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase text-dim">Variant name</label>
              <input value={charForm.name} onChange={(e) => setCharForm({ ...charForm, name: e.target.value })} placeholder="e.g. Wavy Layers"
                data-testid="char-name" className="w-full bg-ink2 border-3 border-black text-cream font-mono text-xs p-2 mt-1" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase text-dim">Finished character PNG (transparent)</label>
              <input ref={charInputRef} type="file" accept="image/png" onChange={(e) => doCharUpload(e.target.files)}
                data-testid="char-file-input" className="block w-full text-[11px] text-cream mt-1 file:mr-3 file:border-3 file:border-black file:bg-neon file:text-black file:px-3 file:py-1.5 file:font-bold file:uppercase" />
              {charUploading && <p className="font-mono text-[10px] text-gold mt-1">Uploading…</p>}
              {charForm.src && (
                <div className="mt-2 inline-block border-3 border-black p-1" style={{ backgroundImage: "linear-gradient(45deg,#333 25%,transparent 25%,transparent 75%,#333 75%),linear-gradient(45deg,#333 25%,#222 25%,#222 75%,#333 75%)", backgroundSize: "16px 16px", backgroundPosition: "0 0,8px 8px" }}>
                  <img src={imgUrl(charForm.src)} alt="variant preview" className="max-h-40" data-testid="char-preview" />
                </div>
              )}
            </div>
            <label className="flex items-center gap-2 font-mono text-[11px] text-cream/80">
              <input type="checkbox" checked={charForm.isDefault} className="w-4 h-4 accent-neon"
                onChange={(e) => setCharForm({ ...charForm, isDefault: e.target.checked })} data-testid="char-default" />
              Make this the default character for this pose + gender
            </label>
            <button onClick={() => saveChar.mutate(charForm)} disabled={!charForm.poseId || !charForm.name || !charForm.src || saveChar.isPending}
              className="btn-neon disabled:opacity-40 disabled:cursor-not-allowed" data-testid="char-save">Add character variant</button>
            {(!charForm.poseId || !charForm.name || !charForm.src) && (
              <p className="font-mono text-[10px] text-gold" data-testid="char-save-hint">
                Add {[!charForm.poseId && "a pose", !charForm.name && "a name", !charForm.src && "an image"].filter(Boolean).join(", ")} to add.
              </p>
            )}
          </div>
        </TabsContent>


        <TabsContent value="moment" className="mt-6">
          <MomentAdminPanel />
        </TabsContent>

        <TabsContent value="creative" className="mt-6" data-testid="tab-content-creative">
          <CreativeAnalyticsPanel />
        </TabsContent>

        <TabsContent value="ai-usage" className="mt-6">
          <AiUsagePanel />
        </TabsContent>

        <TabsContent value="audit" className="mt-6">
          <div className="comic-card p-0 overflow-hidden">
            {logs.length === 0 && <p className="font-mono text-sm text-dim p-6">No audit entries yet.</p>}
            {logs.map((l) => (
              <div key={l.id} className="px-5 py-3 border-b border-charcoal flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="font-display font-900 text-neon text-xs uppercase">{l.action}</span>
                <span className="font-mono text-[10px] text-dim">{l.admin_email}</span>
                <span className="font-mono text-[10px] text-dim ml-auto">{new Date(l.timestamp).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
